import { defineComponent } from 'vue'
import { Location, Route } from 'vue-router'

export type RouteParamValueT = string | number | boolean | undefined | string[] | null
export type RouteQueryParamsT = Record<string, RouteParamValueT>

/**
 * Unmarshall a string into a number or boolean based on certain heuristics
 *
 * @param {string} value
 *
 * @return {string | number | boolean}
 */
function unmarshall(value: string): string | number | boolean {
  if (Number.isFinite(Number(value))) {
    return Number(value)
  }
  if (value === 'false') return false
  if (value === 'true') return true

  return value
}

export default defineComponent({
  name: 'RouteQueryMixin',
  data() {
    return {
      updateQueue: [] as Array<RouteQueryParamsT>,
      isProcessingQueue: false,
      pendingWrite: null as Promise<void> | null,
      debounceTimeout: null as number | null | NodeJS.Timeout
    }
  },
  computed: {
    queryParams: {
      get(this: { $route: Route }): RouteQueryParamsT {
        const { query } = this.$route
        const parsedQuery: RouteQueryParamsT = {}

        for (const key of Object.keys(query)) {
          parsedQuery[key] = unmarshall(String(query[key]))
        }

        return parsedQuery
      },
      set(params: RouteQueryParamsT) {
        if (process.server) {
          return
        }
        this.enqueueUpdate(params)
      }
    }
  },

  methods: {
    enqueueUpdate(newParams: RouteQueryParamsT) {
      this.updateQueue.push(newParams)
      this.debounceProcessQueue()
    },

    // Debounced queue processing (without lodash)
    debounceProcessQueue() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout) // Clear any existing timeout
      }

      this.debounceTimeout = setTimeout(() => {
        this.debounceTimeout = null // Reset timeout ID
        this.processQueue()
      }, 50)
    },

    async processQueue() {
      if (this.isProcessingQueue) return
      this.isProcessingQueue = true

      if (this.pendingWrite) {
        await this.pendingWrite // Wait for pending routes
      }

      let mergedParams: RouteQueryParamsT = { ...this.queryParams } // Start with current params

      while (this.updateQueue.length > 0) {
        const nextUpdate = this.updateQueue.shift() // Get and remove the next update
        mergedParams = { ...mergedParams, ...nextUpdate } // Merge updates
      }

      Object.keys(mergedParams).forEach(
        (k) =>
          ([null, undefined, ''] as unknown[]).includes(mergedParams[k]) && delete mergedParams[k]
      )

      this.pendingWrite = this.setRouteQueryParams(mergedParams as Location['query'])
      try {
        await this.pendingWrite
      } finally {
        this.pendingWrite = null
        this.isProcessingQueue = false
      }
    },

    async setRouteQueryParams(query: Location['query']): Promise<void> {
      await this.$router.replace({ query })
      await this.refetchAfterRouteChange()
    },

    async refetchAfterRouteChange(): Promise<void> {
      await this.$fetch()
    },

    addFilters(newParams: Record<string, string | number | null | string[]>) {
      const cleanedParams: Record<string, string | number | null | string[]> = {}
      for (const key of Object.keys(newParams)) {
        cleanedParams[key] = newParams[key]
      }
      this.queryParams = cleanedParams
    },

    removeFilter(key: string) {
      this.queryParams = { [key]: null }
    }
  }
})
